<template>

  <div class="d-flex" :class="{'low-noise': showConfirm}">
    <div class="d-flex flex-column w-75 p-5">

      <template v-if="! allUploaded">

          <div class="d-flex justify-content-start p-1">
            <Toggle v-model="shouldUploadModel" :disabled="$props.hasUploadedSomeModels" />
            <label class="ms-2">Abilita il caricamento dei files accessori</label>
          </div>

        <!-- Models -->
        <template v-if="shouldUploadModel">
          <div class="text-start">
            <h3 class="d-flex">{{ $t('Select the supplementary files to upload') }}</h3>
            <div class="d-flex">
              <div
                v-for="config in modelConfigs"
                :key="config"
                style="width: 100px; height: 75px;"
                class="text-center">
                <img class="w-100 h-100 cp"
                  :title="`${config.name}`"
                  :src="require(`@/assets/icons/model/${config.icon}-plus.svg`)"
                  @click="$emit('add-model', config.assetType)"
                />
                <span class="text-nowrap">{{ config.name }}</span>
              </div>
            </div>
          </div>
          <div class="d-flex flex-wrap overflow-auto mt-4">
            <RenderAssetIcons :elements="$props.elements" @select="selectModel" :showModels="true" @remove="remove"/>
          </div>
        </template>
        <!-- Projects -->
        <h3 v-if="showProjectsTitle" class="d-flex">Seleziona i progetti da caricare</h3>
        <div class="d-flex flex-wrap overflow-auto">
          <RenderAssetIcons :elements="$props.elements" @select="select" :showProjects="true" @remove="remove"/>
        </div>

        <template v-if="(idsToUpload.length > 0 && ! newBridge)">
          <!-- Materials select for project-->
          <div class="d-flex">
            <div class="input-group mt-4">
            <label class="input-group-text" for="material-selected">
              Materiale predefinito:<span class="fw-bold ms-2 text-uppercase">{{ defaultMaterial }}</span>
            </label>
            <select v-show="showMaterialSelect" id="material-selected" class="form-select" v-model="selectedMaterial.slug">
              <option :value="null">Seleziona il materiale</option>
              <option v-for="(material, index) in allowedMaterials" :value="material.slug" :key="index">
                {{ material.name }}
              </option>
            </select>
          </div>

          <div v-if="showSelectedFinish" class="input-group mt-4 justify-content-end">
            <span class="me-2">Tipo di finitura: </span>
            <label class="me-2 fw-bold">{{$t('Not matte')}}</label>
              <Toggle v-model="isMatteFinish" />
              <label class="ms-2 fw-bold">{{$t('Matte')}}</label>
            </div>
          </div>
          <!-- Colors select -->
          <div class="input-group mt-4">
            <select id="color-selected" class="form-select" v-model="selectedColorId">
              <option :value="null">Seleziona il colore</option>
              <option v-for="(color, index) in colors" :value="color.id" :key="index">
                {{ color.code }}
              </option>
            </select>
          </div>
        </template>

        <!-- Drag and Drop -->
        <div class="d-flex mt-5">
          <DragAndDropArea
            :title="'Trascina i file per caricare'"
            :subtitle="'oppure'"
            :bridgeStyle="newBridge"
            :upload="idsToUpload"
            :model="modelToUpload"
            :material="selectedMaterial"
            :isMatte="isMatteFinish"
            :colorId="selectedColorId"
            @createBridge="confirmToCreateBridge"
            @assetIdCreated="readAssetIdCreated"
            @resetSelection="resetIdsToUpload"
            @sync-models="syncModels"
          />
        </div>

      </template>

      <!-- Completed template -->
      <template v-else>
        <div class="d-flex justify-content-center flex-column align-items-center h-100 w-100">
          <h3 class="d-flex">Tutti gli elementi sono stati caricati correttamente</h3>
          <div class="p-5">
            <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" fill="#74C686" class="bi bi-check2-circle" viewBox="0 0 16 16">
              <path d="M2.5 8a5.5 5.5 0 0 1 8.25-4.764.5.5 0 0 0 .5-.866A6.5 6.5 0 1 0 14.5 8a.5.5 0 0 0-1 0 5.5 5.5 0 1 1-11 0z"/>
              <path d="M15.354 3.354a.5.5 0 0 0-.708-.708L8 9.293 5.354 6.646a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l7-7z"/>
            </svg>
          </div>
        </div>
      </template>

    </div>

    <div class="d-flex flex-column w-25 bg-light p-2 align-items-center justify-content-between">
      <FilesUploaded
        :data="$props.elements"
        :allUploaded="allUploaded"
        @assetsIdsRemoved="readAssetsIdsRemoved"
      />
    </div>

  </div>

  <div v-if="showConfirm" class="d-flex position-absolute top-50 start-50 translate-middle justify-content-center align-items-center flex-column bg-white border shadow border-4 rounded p-4">
    <h4>Il materiale selezionato è diverso da quello prefedefinito.</h4>
    <p>Sei sicuro di volerlo cambiare?</p>
    <div class="modal-footer p-1 border-0 justify-content-center">
      <button type="button" class="btn btn-violet text-uppercase mt-4" @click="showConfirm = false">si</button>
      <button type="button" class="btn btn-outline-violet text-uppercase mt-4" @click="restoreMaterial()">no</button>
    </div>
  </div>

</template>

<script>
import { toRaw, ref, onMounted, watch, inject, reactive, computed } from 'vue';
import _ from "lodash";
import Toggle from '@vueform/toggle';

import DragAndDropArea from "./DragAndDropArea";
import FilesUploaded from "./FilesUploaded";

import { useAssets } from "@/composables/useAssets";
import { index as fetchAllMaterials } from "@/use/repositories/materials/index";
import { fetchAll as fetchAllColors } from "@/use/repositories/colors/fetchAll";
import RenderAssetIcons from '../RenderAssetIcons.vue';

export default {
  name: "Upload",
  components: {
    FilesUploaded,
    DragAndDropArea,
    Toggle,
    RenderAssetIcons
  },
  props: {
    elements: {
      type: Object,
      required: true
    },
    showProjectsTitle: {
      type: Boolean,
      required: true,
    },
    showModelsTitle: {
      type: Boolean,
      required: true,
    },
    hasUploadedSomeModels: {
      type: Boolean,
      default: false,
    }
  },
  emits: [
    'removeBridge',
    'createBridge',
    'asset',
    'assetsIdsRemoved',
    'reset',
    'allFilesUploaded',
    'clear-models-selected',
    'sync-models',
    'add-model',
    'remove-model',
    'force-models-upload',
    'select'

  ],
  setup(props, {emit}) {
    const { findIconByPrescriptionRows } = useAssets();
    const newBridge = ref(false);
    const prepareIdsForBridge = ref([]);
    const idsBridge = ref([]);
    const idsToUpload = ref([]);
    const allMaterials = ref([]);
    const colors = ref([]);
    const selectedMaterial = reactive({
      id: undefined,
      slug: undefined,
    });
    const defaultMaterial = ref();
    const showMaterialSelect = ref(true);
    const selectedColorId = ref(null);
    const showConfirm = ref(false);
    const modelToUpload = ref([]);
    const isModel = ref(false);
    const prefillMaterial = ref(false);
    const prefillColor = ref(false);
    const { materials, modelConfigs } = inject("constants");
    const modelTypes = ["arch", "gum", "stump", "custom-pin", "gig", "bracket", "bar", "base-arch"];
    const allowedMaterialsForFinish = [materials.ZIRCONIA, materials.RESIN];
    const isMatteFinish = ref(false);
    const allowedMaterials = computed(() => {
      return allMaterials.value.filter((material) => material.slug !== materials.PRINTED_RESIN);
    })
    const showSelectedFinish = computed(() => {
      return allowedMaterialsForFinish.includes(selectedMaterial.slug);
    });

    const shouldUploadModel = ref(props.elements.models.must_upload);

    const handleMaterial = () => {
      const materials = props.elements.default_materials || [];

      if (materials.length) {
        const firstMaterial = _.head(materials);

        selectedMaterial.id = firstMaterial.id;
        selectedMaterial.slug = firstMaterial.slug;
        defaultMaterial.value = firstMaterial.name;

        if (materials.length === 1) {
          showMaterialSelect.value = false;
        } else {
          allMaterials.value = materials;
        }

        return;
      }

      // If there are no default materials, retrieve the prescription material.
      defaultMaterial.value = _.kebabCase(props.elements.prescription_material);

      const material = allowedMaterials.value.find(item => item.slug === defaultMaterial.value);

      if (material) {
        Object.assign(selectedMaterial, { id: material.id, slug: material.slug });
      }
    };

    watch(() => shouldUploadModel.value, (value) => {
      emit('force-models-upload', value);
    });


    watch(() => selectedMaterial, (value) => {
      // Destruct
      const { slug } = value;

      if (slug !== "resina-stampata") {
        // Pre selection
        if (! showMaterialSelect.value) return;

        // Check default material
        if (slug !== _.kebabCase(props.elements.prescription_material)) {
          // Show confirm window
          showConfirm.value = true;
        }
      }

      // Update selected material id
      const selectedItem = allMaterials.value.find((item) => item.slug === slug);

      if (selectedItem) {
        selectedMaterial.id = selectedItem.id;
      }
    }, {
      deep: true,
    });

    watch(() => idsToUpload.value.length, (value) => {
      if (value === 0) {
        resetAssetParameters();
      }
    });

    const resetMaterial = () => {
      selectedMaterial.id = props.elements.models?.material?.id ?? null;
      selectedMaterial.slug = props.elements.models?.material?.slug ?? null;
    }

    const resetAssetParameters = () => {
      selectedColorId.value = props.elements.prescription_color_id;
      if (! props.elements.default_materials) {
        resetMaterial();
      };
    }

    const prefillAssetParameters = () => {
      selectedColorId.value = props.elements.models.prescription.colorScale.id;
      resetMaterial();
    }

    // Render icons
    const getIcons = (tooth) => {
      // Render only tooth with empty assets
      if (tooth.assets.length > 0) {
        return;
      }

      return findIconByPrescriptionRows(tooth);
    }

    // Read and sent ids to Icon component
    const getIds = (array) => {
      let ids = [];
      array.forEach((el) => {
        ids.push(el.id);
      });
      return ids;
    }

    // Choose icon action.
    const select = (array) => {
      // No cross selected with models
      // Reset models selection if exists
      if (modelToUpload.value.length !== 0) {
        isModel.value = false;
        emit("clear-models-selected");
      }

      // Clear model to upload
      modelToUpload.value = [];

      handleMaterial();

      resetAssetParameters();

      // If elements > 1 is a bridge
      let isBridge = array.length > 1;

      // If elements === 1 is a tooth/arch/sest/emiarch
      let isTooth = array.length === 1;

      if (isBridge) {
        console.log("BRIDGE");
        if (idsToUpload.value.length) {
          resetSelection();
        }

        // Sync ids
        array.forEach((el) => {
          // Toggle ids bridge
          let indexIdsBridge = idsBridge.value.findIndex((item) => item.id === el.id);
          let indexIdsToUpload = idsToUpload.value.findIndex((item) => item === el.id);

          if (indexIdsBridge !== -1) {
            idsBridge.value.splice(indexIdsBridge, 1);
            idsToUpload.value.splice(indexIdsToUpload, 1);
            emit('select', el.id);
          } else {
            emit('select', el.id);
            idsBridge.value.push(el);
            idsToUpload.value.push(el.id);
          }
        });
      }

      if (isTooth) {
        if (idsBridge.value.length > 0) {
          resetSelection();
        }

        // Get tooth
        let tooth = _.head(array);

        console.log("IS TOOTH");
        syncPrepareIdsForBridge(tooth);

        // If a tooth is not sequential in the selection of the bridge
        if (prepareIdsForBridge.value.length > 1) {
          if (isPossibleCreateBridge(prepareIdsForBridge.value[prepareIdsForBridge.value.length - 1])) {
            newBridge.value = true;
          } else {
            newBridge.value = false;
            resetSelection();
            syncPrepareIdsForBridge(tooth);
          }
        } else {
          newBridge.value = false;
        }

        syncIds(idsToUpload, tooth.id);
      }
    }

    watch(() => isModel.value, (value) => {
      if (value) {
        // Prefill asset info
        prefillAssetParameters();
      }
    });

    const selectModel = (model) => {
      let selectionRemember = model.selected;

      // Reset all models selected for toggle effect
      emit("clear-models-selected");

      // Reset tooth and bridge selected if exists
      if (idsToUpload.value.length !== 0) {
        resetSelection();
      }

      if (selectionRemember === false) {
        // Prefill and disabled material and color
        prefillMaterial.value = true;
        prefillColor.value = true;
        // Model is selected
        isModel.value = true;
        model.selected = true;
        // Clear model to upload
        modelToUpload.value = [];
        // Prepare model to upload
        modelToUpload.value.push(model);
      }

      if (selectionRemember) {
        model.selected = false;
        // Clear and enabled material and color
        prefillMaterial.value = false;
        prefillColor.value = false;
        // Model not selected
        isModel.value = false;
        // Clear model to upload
        modelToUpload.value = [];
      }
    }

    const syncIds = (array, id) => {
      if (array.value.includes(id)) {
        // Remove tooth ids
        array.value.splice(array.value.indexOf(id), 1);
      } else {
        // Add tooth ids
        array.value.push(id);
      }
    }

    const syncPrepareIdsForBridge = (obj) => {
      // Sync ids tooth
      let index = prepareIdsForBridge.value.findIndex((el) => el.id === obj.id);
      if (index !== -1) {
        obj.selected = false;
        // Remove obj
        prepareIdsForBridge.value.splice(index, 1);
      } else {
        obj.selected = true;
        // Add obj
        prepareIdsForBridge.value.push(obj);
      }
    }

    // Check if possible create a bridge
    const isPossibleCreateBridge = (currentTooth) => {
      // If the last tooth is not sequential
      let lastZoneNumber = parseInt(
        prepareIdsForBridge.value[prepareIdsForBridge.value.length - 2]?.zone_number
      );
      let currentZoneNumber = parseInt(currentTooth.zone_number);

      // Create array with only zone number
      let zoneNumbers = [];
      prepareIdsForBridge.value.forEach((el) => {
        zoneNumbers.push(parseInt(el.zone_number));
      });

      // If the current value is below the threshold
      if (
        (currentZoneNumber === 11 && lastZoneNumber === 21) ||
        (currentZoneNumber === 21 && lastZoneNumber === 11)
      ) {
        return true;
      }

      // If the current value is above the threshold
      if (
        (currentZoneNumber === 31 && lastZoneNumber === 41) ||
        (currentZoneNumber === 41 && lastZoneNumber === 31)
      ) {
        return true;
      }
      // Convert negative value to positive
      return Math.abs(lastZoneNumber - currentZoneNumber) == 1;
    }

    // Read to user choose bridge's action
    const confirmToCreateBridge = (value) => {
      if (value) {
        // Create
        let ids = [];
        prepareIdsForBridge.value.forEach((obj) => ids.push(obj.id));
        emit('createBridge', ids);
      }

      // Remove selected style
      resetSelection();
    }

    const allUploaded = computed(() => {
      const bridgesOk = props.elements.bridges.every(el => _.head(el.tooth)?.assets?.length);

      const teethOk = props.elements.tooth.every(el => el.assets?.length);

      const modelsOk = modelTypes.every(type => {
          const elements = props.elements.models[_.camelCase(type)]?.elements || [];
          return elements.every(el => el.assets?.length);
        });

      const result = bridgesOk && teethOk && modelsOk;

      emit('allFilesUploaded', result);

      return result;
    });


    const resetSelection = () => {
      prepareIdsForBridge.value = [];
      idsToUpload.value = [];
      idsBridge.value = [];
      newBridge.value = false;
      emit('reset');
    }

    const readAssetIdCreated = (obj) => {
      emit('asset', obj);
      resetSelection();
      modelToUpload.value = [];
    }

    const readAssetsIdsRemoved = (ids) => {
      emit('assetsIdsRemoved', ids);
      resetSelection();
    }

    const resetIdsToUpload = (value) => {
      idsToUpload.value = value;
      if (! props.elements.default_materials.length) {
        resetMaterial();
      }
      selectedColorId.value = props.elements.prescription_color_id;
    }

    const restoreMaterial = () => {
      if (! props.elements.default_materials.length) {
        resetMaterial();
      }

      selectedColorId.value = props.elements.prescription_color_id;
      showConfirm.value = false;
    }

    const syncModels = (obj) => {
      console.log("SYNC MODELS", obj);

      emit("sync-models", obj);
      isModel.value = false;
      emit("clear-models-selected");
    }

    const remove = (event) => {
      if (event.event_type === "remove-model") selectModel(event.element);
      resetSelection();
      return emit(event.event_type, event?.data)
    }

    onMounted(async () => {
      // Get all colors
      try {
        colors.value = (await fetchAllColors()).colors;
      } catch (error) {
        console.error(error);
      }

      handleMaterial();

      if (! allMaterials.value.length) {
        try {
        allMaterials.value = (await fetchAllMaterials()).materials;
        } catch (error) {
          console.error(error);
        }
      }
    });

    return {
      getIcons,
      select,
      toRaw,
      newBridge,
      confirmToCreateBridge,
      prepareIdsForBridge,
      getIds,
      idsToUpload,
      idsBridge,
      readAssetIdCreated,
      readAssetsIdsRemoved,
      resetIdsToUpload,
      allUploaded,
      colors,
      selectedMaterial,
      defaultMaterial,
      selectedColorId,
      showConfirm,
      restoreMaterial,
      modelToUpload,
      selectModel,
      prefillMaterial,
      prefillColor,
      syncModels,
      shouldUploadModel,
      showMaterialSelect,
      allowedMaterials,
      remove,
      allowedMaterialsForFinish,
      modelConfigs,
      modelTypes,
      showSelectedFinish,
      isMatteFinish,
    }
  }
}
</script>

<style scoped>
 #stump-plus:hover,
 #gum-plus:hover,
 #custom-pin-plus:hover {
   cursor: pointer;
 }
</style>
